// src/components/DealModal.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import dayjs from 'dayjs';

import {
  DEFAULT_HIDDEN_COLUMNS,
  dealLabel,
  formatStructuredValue,
  isBooleanType,
  isColumnEditable,
  isDateType,
  isDateTimeType,
  isLongTextColumn,
  isNumericType,
  isStructuredType,
  parseNumericInput,
} from '../utils/dealTeamFields';

// Intake writes whatever the email said, so a column's width cannot be fixed by
// its name: SPONSOR is usually a few words and occasionally a paragraph. Each
// field claims a share of the row from what it actually holds, and every text
// field is a textarea that grows to its content, so nothing is shown clipped to
// a single line the reader has to scroll inside.
const FIELD_SPANS = {
  short: '1 1 260px',
  medium: '1 1 440px',
  wide: '1 1 100%',
};
const MEDIUM_FIELD_LENGTH = 45;
const WIDE_FIELD_LENGTH = 120;
// Beyond this the field scrolls internally rather than pushing the dialog's own
// controls out of reach.
const MAX_GROWN_ROWS = 16;
const MAX_PAYLOAD_ROWS = 20;

const fieldSpan = (column, value) => {
  if (isStructuredType(column.data_type)) return 'wide';
  if (isBooleanType(column.data_type) || isNumericType(column.data_type) ||
    isDateType(column.data_type) || isDateTimeType(column.data_type)) return 'short';
  const text = value === null || value === undefined ? '' : String(value);
  if (text.length > WIDE_FIELD_LENGTH || text.includes('\n')) return 'wide';
  if (text.length > MEDIUM_FIELD_LENGTH) return 'medium';
  return 'short';
};

// An outlined label sits in the border notch, so a narrow field would clip a
// long column name before the value ever mattered.
const minFieldWidth = (label) => Math.max(240, Math.round(label.length * 9.5) + 48);

const labelFor = (name) => String(name).replaceAll('_', ' ');

const normalizeText = (value) =>
  value === null || value === undefined ? '' : String(value);

// NULL and false are different deal states — a BOOLEAN column that nothing has
// answered yet is not an answer of "no" — so the form carries three values and
// compares them directly rather than through Boolean().
const normalizeTriState = (value) => (value === null || value === undefined ? null : value);

// A cleared field and a NULL column are the same deal state, so neither counts
// as an edit; without this every open deal with a NULL column would look dirty.
const looseEqual = (column, left, right) => {
  if (isNumericType(column.data_type)) {
    return parseNumericInput(left) === parseNumericInput(right);
  }
  if (isBooleanType(column.data_type)) {
    return normalizeTriState(left) === normalizeTriState(right);
  }
  return normalizeText(left) === normalizeText(right);
};

// A date input only accepts YYYY-MM-DD, so a DATE column that arrives with a
// time part would render blank and read as a missing date. Display just the
// date; state keeps what the row holds until an editor picks a new one, so an
// untouched column is not counted as an edit.
const toDateInputValue = (value) => {
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(normalizeText(value));
  return match ? match[1] : '';
};

// Form state holds what the input shows (a string for text/number/date, a bool
// for a checkbox); this turns it back into the value the column stores.
const coerceForColumn = (column, value) => {
  if (isBooleanType(column.data_type)) {
    return value === null || value === undefined ? null : Boolean(value);
  }
  if (isNumericType(column.data_type)) return parseNumericInput(value);
  if (isDateType(column.data_type)) return value === '' ? null : value;
  return value;
};

const initialFormState = (columns, row) => Object.fromEntries(columns.map((column) => {
  const value = row?.[column.name];
  if (isBooleanType(column.data_type)) return [column.name, normalizeTriState(value)];
  return [column.name, normalizeText(value)];
}));

/**
 * Full-deal form for one CRE deal-team row.
 *
 * The workspace is metadata-driven, so this renders whatever columns the
 * backend reports rather than a fixed field list. Editable columns come first;
 * intake provenance, timestamps and semi-structured payloads follow as
 * read-only context, because they are either facts about the message the deal
 * arrived in or values the append-only contract manages server-side.
 *
 * Saving hands the parent a complete deal state — the loaded row with the
 * edited columns merged in — since the append endpoint rejects a partial row.
 */
const DealModal = ({
  open,
  onClose,
  onSave,
  metadata,
  row,
  dropdownValues = {},
  canWrite = false,
  isSaving = false,
  blockedReason = '',
}) => {
  const columns = useMemo(() => metadata?.columns || [], [metadata]);
  const [formData, setFormData] = useState({});
  const [touchedFields, setTouchedFields] = useState(() => new Set());
  const keyColumn = metadata?.key_column;
  const keyValue = keyColumn ? row?.[keyColumn] : undefined;

  // Reset on open and when a different deal is opened.
  useEffect(() => {
    if (!open || !row) return;
    setTouchedFields(new Set());
    setFormData(initialFormState(columns, row));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, keyValue, metadata?.name]);

  // When the underlying row reloads while the modal is open (e.g. after a 409
  // conflict), rebase untouched fields onto the fresh row while preserving any
  // field the user actually edited in this form session.
  useEffect(() => {
    if (!open || !row) return;
    const freshState = initialFormState(columns, row);
    setFormData((current) => {
      const next = { ...freshState };
      touchedFields.forEach((field) => {
        if (field in current) {
          next[field] = current[field];
        }
      });
      return next;
    });
  }, [columns, open, row, touchedFields]);

  const handleChange = useCallback((name, value) => {
    setTouchedFields((current) => {
      const next = new Set(current);
      next.add(name);
      return next;
    });
    setFormData((current) => ({ ...current, [name]: value }));
  }, []);

  const editableColumns = useMemo(
    () => columns.filter((column) =>
      isColumnEditable(column, metadata, canWrite) &&
      !DEFAULT_HIDDEN_COLUMNS.has(column.name)),
    [canWrite, columns, metadata],
  );
  const contextColumns = useMemo(
    () => columns.filter((column) => !editableColumns.includes(column)),
    [columns, editableColumns],
  );

  // Only columns the editor actually changed are merged, so an untouched NULL
  // is appended as NULL instead of being coerced to '' or false.
  const changes = useMemo(() => Object.fromEntries(
    editableColumns
      .map((column) => [column, coerceForColumn(column, formData[column.name])])
      .filter(([column, value]) => !looseEqual(column, value, row?.[column.name]))
      .map(([column, value]) => [column.name, value])
  ), [editableColumns, formData, row]);

  const isEditable = Boolean(canWrite && metadata?.writable && editableColumns.length > 0);
  const isDirty = Object.keys(changes).length > 0;

  const handleSave = useCallback(() => {
    onSave({ ...row, ...changes });
  }, [changes, onSave, row]);

  if (!open || !row || !metadata) return null;

  const titleName = dealLabel(row, metadata);

  const renderInput = (column, editable) => {
    const { name, data_type: dataType } = column;
    const label = labelFor(name);
    const value = formData[name] ?? '';
    // STATUS is not editable, but it still renders as a (disabled) select so a
    // reader sees the row state the same way everywhere.
    const vocabulary = dropdownValues[name] ||
      (name === metadata.status_column ? metadata.status_values : null);
    // The seeded vocabulary collapses spellings case-insensitively, so a deal
    // holding 'New' against a seeded 'NEW' — or a NULL against a status list —
    // would bind an out-of-range value and render blank, reporting a stage the
    // deal does not have. Offer the deal's own value so it shows as it is
    // stored; picking a canonical spelling is then a deliberate edit.
    const choices = vocabulary && !vocabulary.includes(value)
      ? [value, ...vocabulary]
      : vocabulary;

    if (isStructuredType(dataType)) {
      return (
        <TextField
          fullWidth
          size="small"
          label={label}
          value={formatStructuredValue(row[name])}
          disabled
          multiline
          rows={Math.min(
            MAX_PAYLOAD_ROWS,
            Math.max(4, formatStructuredValue(row[name]).split('\n').length),
          )}
          InputLabelProps={{ shrink: true }}
          inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.75rem' } }}
          // A kilobyte payload cannot fit any default, so MUI's resize: none is
          // lifted here and the reader can drag the field open.
          sx={{ '& textarea': { resize: 'vertical', overflowWrap: 'anywhere' } }}
        />
      );
    }

    if (isDateTimeType(dataType)) {
      const stamp = row[name] ? dayjs(row[name]) : null;
      return (
        <TextField
          fullWidth
          size="small"
          label={label}
          value={stamp && stamp.isValid() ? stamp.format('YYYY-MM-DD HH:mm') : ''}
          disabled
          InputLabelProps={{ shrink: true }}
        />
      );
    }

    if (isBooleanType(dataType)) {
      return (
        <FormControlLabel
          control={(
            <Checkbox
              checked={formData[name] === true}
              // An unanswered column reads as indeterminate rather than as a
              // "no"; the first click answers true, the next answers false, the
              // way the grid's checkbox editor moves a NULL cell.
              indeterminate={normalizeTriState(formData[name]) === null}
              disabled={!editable}
              onChange={(event) => handleChange(name, event.target.checked)}
            />
          )}
          label={label}
        />
      );
    }

    if (choices) {
      return (
        <FormControl fullWidth size="small" disabled={!editable}>
          <InputLabel id={`deal-${name}-label`} shrink>{label}</InputLabel>
          <Select
            labelId={`deal-${name}-label`}
            label={label}
            value={value}
            disabled={!editable}
            notched
            displayEmpty
            onChange={(event) => handleChange(name, event.target.value)}
          >
            {choices.map((choice) => (
              <MenuItem key={choice} value={choice}>{choice || ' '}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    // Only text grows: a number or a date has a bounded width, and switching an
    // input to a textarea mid-edit would remount it and drop the caret, so a
    // text column starts as a one-row textarea and grows with what is typed.
    const isFreeText = !isNumericType(dataType) && !isDateType(dataType);
    const startsTall = isLongTextColumn(name, row[name]);

    return (
      <TextField
        fullWidth
        size="small"
        label={label}
        value={isDateType(dataType) ? toDateInputValue(value) : value}
        disabled={!editable}
        type={isNumericType(dataType) ? 'number' : isDateType(dataType) ? 'date' : 'text'}
        multiline={isFreeText}
        minRows={isFreeText ? (startsTall ? 3 : 1) : undefined}
        maxRows={isFreeText ? MAX_GROWN_ROWS : undefined}
        InputLabelProps={{ shrink: true }}
        onChange={(event) => handleChange(name, event.target.value)}
        // An unbroken token — a URL, a base64 id — wraps instead of scrolling
        // sideways out of view.
        sx={isFreeText ? { '& textarea': { overflowWrap: 'anywhere' } } : undefined}
      />
    );
  };

  const renderSection = (sectionColumns, editable) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
      {sectionColumns.map((column) => {
        const label = labelFor(column.name);
        // Size from what the editor is looking at now, so a field that was
        // opened short and then filled with a paragraph still claims the room.
        const span = fieldSpan(column, formData[column.name] ?? row[column.name]);
        return (
          <Box
            key={column.name}
            data-field-span={span}
            sx={{
              flex: FIELD_SPANS[span],
              maxWidth: '100%',
              minWidth: span === 'wide' ? '100%' : minFieldWidth(label),
            }}
          >
            {renderInput(column, editable)}
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">
              {isEditable ? `Edit deal — ${titleName}` : `Deal — ${titleName}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {`CRE.${metadata.name}`}
              {keyColumn ? ` · ${labelFor(keyColumn)} ${keyValue}` : ''}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close deal">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {blockedReason && <Alert severity="warning" sx={{ mb: 2 }}>{blockedReason}</Alert>}
        {metadata.writable && !canWrite && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You can review this deal, but saving requires the cre.loans:write permission.
          </Alert>
        )}
        {!metadata.writable && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {`CRE.${metadata.name} is read-only`}
            {(metadata.missing_write_columns || []).length
              ? ` because it is missing the append-only contract column(s): ${
                metadata.missing_write_columns.join(', ')}.`
              : '.'}
          </Alert>
        )}

        {editableColumns.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Deal</Typography>
            {renderSection(editableColumns, true)}
          </>
        )}
        {contextColumns.length > 0 && (
          <>
            <Typography
              variant="subtitle2"
              sx={{ mt: editableColumns.length > 0 ? 3 : 0, mb: 1 }}
            >
              {editableColumns.length > 0 ? 'Source & system' : 'Deal'}
            </Typography>
            {renderSection(contextColumns, false)}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {isEditable && isDirty && (
          <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>
            {`${Object.keys(changes).length} field(s) changed`}
          </Typography>
        )}
        <Button onClick={onClose} color="secondary">Cancel</Button>
        {isEditable && (
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={!isDirty || Boolean(blockedReason) || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DealModal;
